import type { DateRange } from '@library/types/dates';
import { DateTime } from 'luxon';
import { GamePicker } from '@bo-components/simulations/GamePicker';
import { GlobalFilters } from '@library/components/GlobalFilters';
import { Loading } from '@library/components/Loading';
import { NoData } from '@library/components/NoData';
import { PageTitle } from '@library/components/PageTitle';
import { SimulationCTA } from '@bo-components/simulations/SimulationCTA';
import type { SimulationsFilters } from '@library/types/filters';
import { useDashBoard } from '@bo-hooks/useDashboard';
import { useEffect } from 'react';
import { useGames } from '@bo-hooks/useGames';

export const SimulationsHomePage = () => {
  const {
    durations,
    filters,
    fromDate,
    handleSearch,
    handleSelectDateRange,
    handleSelectLeague,
    isLeagueError,
    isLeagueLoading,
    leagueInfo,
    leaguesList,
    selectedEndDate,
    selectedLeagueId,
    selectedStartDate,
    setFilters
  } = useDashBoard({
    dashboardName: 'SIMULATIONS',
    initialStartDate: DateTime.now()
  });

  const { id: leagueId } = leagueInfo ?? {};

  const simulationsFilters = filters as SimulationsFilters;

  const { game: selectedGame } = simulationsFilters;

  // Sending the parent/base leagueId since games are same across the leagues/sub leagues
  // this would avoid multiple api calls when the duration/sub league is changed
  const { games, isLeagueGamesError, isLeagueGamesLoading } = useGames({
    leagueId,
    fromDate,
    toDate: fromDate
  });

  const handleSelectGame = (value: string) => {
    const newFilterState: SimulationsFilters = {
      game: value
    };

    setFilters(newFilterState);
  };

  const handleChangeDate = (value: DateRange) => {
    handleSelectDateRange(value);
  };

  useEffect(() => {
    if (!isLeagueLoading && !isLeagueError && leaguesList.length > 0) {
      const namedId = leaguesList.find((league) => league.name === 'NFL')?.id;

      handleSelectLeague(namedId ?? '');
    }
  }, [handleSelectLeague, isLeagueError, isLeagueLoading, leaguesList]);

  if (isLeagueLoading) {
    return <Loading loadingMessage="Loading Leagues" />;
  }

  if (isLeagueError || !leagueInfo || !leagueId) {
    return <NoData />;
  }

  return (
    <div className="h-full w-auto flex-col">
      <section className="border-soFresh-140 w-auto border-b px-6 pb-2 pt-3">
        <PageTitle title="Simulations" />

        <div className="flex gap-4 py-4">
          <div className="grow">
            <GlobalFilters
              allowPastDates
              dateRange={false}
              durations={durations}
              handleSearch={handleSearch}
              handleSelectDateRange={handleChangeDate}
              handleSelectLeague={() => {}}
              leaguesList={leaguesList}
              searchDisabled={false}
              searchPlaceholder="Search players"
              selectedStartDate={selectedStartDate}
              selectedEndDate={selectedEndDate}
              selectedLeagueId={selectedLeagueId}
              showLeagueSelect={false}
              showSearchInput={false}
            />
          </div>
        </div>

        <GamePicker
          isError={isLeagueGamesError}
          games={games}
          isLoading={isLeagueGamesLoading}
          selectGame={handleSelectGame}
          selectedGame={selectedGame}
        />
      </section>
      <SimulationCTA
        selectedGameId={selectedGame}
        selectedLeagueId={selectedLeagueId}
      />
    </div>
  );
};
